import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import {
  Download,
  Hand,
  Image as ImageIcon,
  Maximize2,
  MousePointer2,
  Pentagon,
  Redo2,
  Route,
  Sparkles,
  Square,
  Type,
  Undo2,
  Upload,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteGuestPitchBooking, canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import {
  SitePlanVectorEditor,
  type EditorSelection,
  type EditorTool,
  type SitePlanVectorEditorHandle,
} from "@/components/site-plan/SitePlanVectorEditor";
import { SitePlanEditorSidebar } from "@/components/site-plan/SitePlanEditorSidebar";
import {
  documentFromPitchPolygons,
  downloadTextFile,
  emptyDocument,
  ensureRoutes,
  exportDocumentToSvg,
  importSvgDocument,
  isDuplicateLabelText,
  loadImageDimensions,
  PLAN_LAYERS,
  prepareImageForVectorize,
  readFileAsText,
  type PlanLayer,
  type PlanRoute,
  type PlanShape,
  type PlanSymbol,
  type PlanText,
  type SitePlanAiStatus,
  type SitePlanDocumentOut,
  type SitePlanDocumentSaveResult,
  type SitePlanDocumentUpdate,
  type SitePlanVectorDocument,
  type SitePlanVectorizeResult,
} from "@/lib/sitePlanDocument";
import type { SitePlanSettings } from "@/lib/sitePlan";
import type { Pitch } from "@/types/camping";

const MAX_HISTORY = 80;
const DEFAULT_PLAN_WIDTH = 1600;
const DEFAULT_PLAN_HEIGHT = 1000;

interface PendingImage {
  dataUrl: string;
  width: number;
  height: number;
  fileName: string;
}

export function SitePlanEditorPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const isAdmin = canWriteStammdaten(groups);
  const canEdit = canWriteGuestPitchBooking(groups);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: "success" | "info" | "warning"; text: string; details?: string[] } | null>(
    null,
  );

  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [aiStatus, setAiStatus] = useState<SitePlanAiStatus | null>(null);
  const [legacyImage, setLegacyImage] = useState<string | null>(null);
  const [savedMeta, setSavedMeta] = useState<{ updated_at: string | null; updated_by: string | null }>({
    updated_at: null,
    updated_by: null,
  });

  const [doc, setDoc] = useState<SitePlanVectorDocument | null>(null);
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [sourceImageDirty, setSourceImageDirty] = useState<"keep" | "replace" | "clear">("keep");
  const [dirty, setDirty] = useState(false);
  const historyRef = useRef<{ past: SitePlanVectorDocument[]; future: SitePlanVectorDocument[] }>({
    past: [],
    future: [],
  });
  const [historyVersion, setHistoryVersion] = useState(0);

  const [tool, setTool] = useState<EditorTool>({ kind: "select" });
  const [revealedPitchId, setRevealedPitchId] = useState<string | null>(null);
  const [selection, setSelection] = useState<EditorSelection | null>(null);
  const [newShapeLayer, setNewShapeLayer] = useState<PlanLayer>("pitch");
  const [showSourceImage, setShowSourceImage] = useState(true);
  const [sourceOpacity, setSourceOpacity] = useState(0.6);
  const [showLabels, setShowLabels] = useState(true);
  const editorHandle = useRef<SitePlanVectorEditorHandle | null>(null);

  const [pendingImage, setPendingImage] = useState<PendingImage | null>(null);
  const [aiHints, setAiHints] = useState("");
  const [vectorizing, setVectorizing] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncPitches, setSyncPitches] = useState(true);
  const [publishBackground, setPublishBackground] = useState(false);
  const [embedSourceInBackground, setEmbedSourceInBackground] = useState(false);
  const [replaceConfirm, setReplaceConfirm] = useState<{ title: string; run: () => void } | null>(null);

  const pitchCodes = useMemo(() => new Map(pitches.map((p) => [p.id, p.code])), [pitches]);
  const pitchCategoryColors = useMemo(
    () => new Map(pitches.map((p) => [p.id, p.category_color])),
    [pitches],
  );
  const duplicateLabelCount = useMemo(
    () => (doc ? doc.texts.filter((text) => isDuplicateLabelText(text, doc, pitchCodes)).length : 0),
    [doc, pitchCodes],
  );

  // --- Laden -----------------------------------------------------------------------
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<SitePlanDocumentOut>("/api/v1/site-plan/document?include_source_image=true"),
      api.get<Pitch[]>("/api/v1/pitches"),
      api.get<SitePlanAiStatus>("/api/v1/site-plan/document/ai-status").catch(() => null),
      api.get<SitePlanSettings>("/api/v1/settings/site-plan").catch(() => null),
    ])
      .then(([documentOut, pitchList, ai, settings]) => {
        setPitches(pitchList.filter((p) => p.is_active));
        setAiStatus(ai);
        setLegacyImage(settings?.image_data ?? null);
        setDoc(documentOut.document ? ensureRoutes(documentOut.document) : null);
        setSourceImage(documentOut.source_image_data ?? null);
        setSavedMeta({ updated_at: documentOut.updated_at, updated_by: documentOut.updated_by });
        historyRef.current = { past: [], future: [] };
        setDirty(false);
        setSourceImageDirty("keep");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "Platzplan-Editor" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // --- Dokument + Historie ------------------------------------------------------------
  // Historie lebt in Refs (keine Seiteneffekte in State-Updatern, StrictMode-sicher).
  const docStateRef = useRef<SitePlanVectorDocument | null>(null);
  docStateRef.current = doc;
  /** Zustand vor einer laufenden Geste (Ziehen) – wird beim Commit in die Historie gelegt. */
  const gestureBaseRef = useRef<SitePlanVectorDocument | null>(null);

  const pushHistory = useCallback((entry: SitePlanVectorDocument) => {
    const past = [...historyRef.current.past, entry];
    if (past.length > MAX_HISTORY) past.shift();
    historyRef.current = { past, future: [] };
    setHistoryVersion((v) => v + 1);
  }, []);

  const applyChange = useCallback(
    (next: SitePlanVectorDocument, commit: boolean) => {
      const current = docStateRef.current;
      if (!commit) {
        if (gestureBaseRef.current === null && current) gestureBaseRef.current = current;
      } else if (current) {
        const base = gestureBaseRef.current ?? current;
        gestureBaseRef.current = null;
        pushHistory(base);
      }
      docStateRef.current = next;
      setDoc(next);
      setDirty(true);
    },
    [pushHistory],
  );

  const replaceDocument = useCallback(
    (next: SitePlanVectorDocument | null, options: { resetHistory?: boolean } = {}) => {
      const current = docStateRef.current;
      gestureBaseRef.current = null;
      if (options.resetHistory || !current) {
        historyRef.current = { past: [], future: [] };
        setHistoryVersion((v) => v + 1);
      } else {
        pushHistory(current);
      }
      docStateRef.current = next;
      setDoc(next);
      setSelection(null);
      setDirty(true);
    },
    [pushHistory],
  );

  const undo = useCallback(() => {
    const current = docStateRef.current;
    const { past, future } = historyRef.current;
    const previous = past[past.length - 1];
    if (!previous || !current) return;
    historyRef.current = { past: past.slice(0, -1), future: [current, ...future] };
    gestureBaseRef.current = null;
    setHistoryVersion((v) => v + 1);
    docStateRef.current = previous;
    setDoc(previous);
    setSelection(null);
    setDirty(true);
  }, []);

  const redo = useCallback(() => {
    const current = docStateRef.current;
    const { past, future } = historyRef.current;
    const next = future[0];
    if (!next || !current) return;
    historyRef.current = { past: [...past, current], future: future.slice(1) };
    gestureBaseRef.current = null;
    setHistoryVersion((v) => v + 1);
    docStateRef.current = next;
    setDoc(next);
    setSelection(null);
    setDirty(true);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT")) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;
  void historyVersion; // erzwingt Re-Render für canUndo/canRedo

  const patchShape = useCallback(
    (id: string, patch: Partial<PlanShape>) => {
      const current = docStateRef.current;
      if (!current) return;
      applyChange({ ...current, shapes: current.shapes.map((s) => (s.id === id ? { ...s, ...patch } : s)) }, true);
    },
    [applyChange],
  );
  const patchSymbol = useCallback(
    (id: string, patch: Partial<PlanSymbol>) => {
      const current = docStateRef.current;
      if (!current) return;
      applyChange({ ...current, symbols: current.symbols.map((s) => (s.id === id ? { ...s, ...patch } : s)) }, true);
    },
    [applyChange],
  );
  const patchText = useCallback(
    (id: string, patch: Partial<PlanText>) => {
      const current = docStateRef.current;
      if (!current) return;
      applyChange({ ...current, texts: current.texts.map((t) => (t.id === id ? { ...t, ...patch } : t)) }, true);
    },
    [applyChange],
  );
  const patchRoute = useCallback(
    (id: string, patch: Partial<PlanRoute>) => {
      const current = docStateRef.current;
      if (!current) return;
      if (patch.pitch_id) setRevealedPitchId(null);
      applyChange(
        { ...current, routes: (current.routes ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r)) },
        true,
      );
    },
    [applyChange],
  );
  const deleteElement = useCallback(
    (target: EditorSelection) => {
      const current = docStateRef.current;
      if (!current) return;
      const next =
        target.kind === "shape"
          ? { ...current, shapes: current.shapes.filter((s) => s.id !== target.id) }
          : target.kind === "symbol"
            ? { ...current, symbols: current.symbols.filter((s) => s.id !== target.id) }
            : target.kind === "route"
              ? { ...current, routes: (current.routes ?? []).filter((r) => r.id !== target.id) }
              : { ...current, texts: current.texts.filter((t) => t.id !== target.id) };
      applyChange(next, true);
      setSelection(null);
    },
    [applyChange],
  );

  // --- Quellen -------------------------------------------------------------------------
  const confirmReplace = useCallback(
    (title: string, run: () => void) => {
      const hasContent = doc && (doc.shapes.length > 0 || doc.symbols.length > 0 || doc.texts.length > 0);
      if (hasContent) setReplaceConfirm({ title, run });
      else run();
    },
    [doc],
  );

  async function onImageFileSelected(file: File | null) {
    if (!file) return;
    setError(null);
    setNotice(null);
    if (!file.type.startsWith("image/") || file.type === "image/svg+xml") {
      setError("Bitte ein Rasterbild wählen (PNG, JPEG, WebP). SVG-Dateien über „SVG importieren“.");
      return;
    }
    try {
      const prepared = await prepareImageForVectorize(file, 2048);
      setPendingImage({ ...prepared, fileName: file.name });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bild konnte nicht gelesen werden.");
    }
  }

  function useImageAsBackgroundOnly() {
    if (!pendingImage) return;
    const image = pendingImage;
    confirmReplace("Neuen Plan aus diesem Bild beginnen?", () => {
      replaceDocument(emptyDocument(image.width, image.height), { resetHistory: true });
      setSourceImage(image.dataUrl);
      setSourceImageDirty("replace");
      setShowSourceImage(true);
      setPendingImage(null);
      setNotice({
        kind: "info",
        text: "Leerer Plan mit dem Bild als Vorlage angelegt. Zeichnen Sie die Flächen nach oder nutzen Sie die KI-Umwandlung.",
      });
    });
  }

  async function vectorizeWithAi() {
    if (!pendingImage || !canEdit) return;
    const image = pendingImage;
    const run = async () => {
      setVectorizing(true);
      setError(null);
      setNotice(null);
      try {
        const result = await api.post<SitePlanVectorizeResult>("/api/v1/site-plan/document/vectorize", {
          image_data: image.dataUrl,
          width: image.width,
          height: image.height,
          hints: aiHints.trim() || null,
          store_source_image: true,
        });
        replaceDocument(result.document, { resetHistory: true });
        setSourceImage(image.dataUrl);
        setSourceImageDirty("keep"); // bereits serverseitig gespeichert
        setShowSourceImage(true);
        setPendingImage(null);
        const details = [...result.warnings];
        if (result.unmatched_labels.length) {
          details.push(`Ohne passenden Stellplatz: ${result.unmatched_labels.join(", ")}`);
        }
        setNotice({
          kind: "success",
          text: `KI-Umwandlung (${result.provider} / ${result.model}): ${result.document.shapes.length} Flächen, ${result.document.symbols.length} Symbole erkannt, ${result.matched_pitches} Stellplätze automatisch zugeordnet. Bitte prüfen und korrigieren, dann speichern.`,
          details,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "KI-Umwandlung fehlgeschlagen.");
      } finally {
        setVectorizing(false);
      }
    };
    confirmReplace("Aktuellen Plan durch das KI-Ergebnis ersetzen?", () => void run());
  }

  async function onSvgFileSelected(file: File | null) {
    if (!file) return;
    setError(null);
    setNotice(null);
    try {
      const text = await readFileAsText(file);
      const { document: imported, warnings } = importSvgDocument(text);
      confirmReplace("Aktuellen Plan durch das importierte SVG ersetzen?", () => {
        replaceDocument(imported, { resetHistory: true });
        setNotice({
          kind: "success",
          text: `SVG importiert: ${imported.shapes.length} Flächen, ${imported.symbols.length} Symbole, ${imported.texts.length} Texte. Ordnen Sie die Platzflächen Stellplätzen zu (Reiter „Plätze“ → automatisch zuordnen).`,
          details: warnings,
        });
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "SVG konnte nicht importiert werden.");
    }
  }

  async function startFromExistingPlan() {
    const image = legacyImage;
    try {
      const dims = image ? await loadImageDimensions(image) : { width: DEFAULT_PLAN_WIDTH, height: DEFAULT_PLAN_HEIGHT };
      confirmReplace("Aktuellen Plan durch die bestehenden Platzflächen ersetzen?", () => {
        const next = documentFromPitchPolygons(pitches, dims.width, dims.height);
        replaceDocument(next, { resetHistory: true });
        if (image) {
          setSourceImage(image);
          setSourceImageDirty("replace");
          setShowSourceImage(true);
        }
        setNotice({
          kind: "info",
          text: `${next.shapes.length} bestehende Platzflächen übernommen${image ? " – das bisherige Planbild dient als Vorlage" : ""}.`,
        });
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bestehender Plan konnte nicht geladen werden.");
    }
  }

  function startBlank() {
    confirmReplace("Aktuellen Plan verwerfen und leer beginnen?", () => {
      replaceDocument(emptyDocument(DEFAULT_PLAN_WIDTH, DEFAULT_PLAN_HEIGHT), { resetHistory: true });
      setSourceImage(null);
      setSourceImageDirty("clear");
    });
  }

  // --- Zuordnung / Speichern / Export --------------------------------------------------
  async function autoMatch() {
    if (!doc || !canEdit) return;
    setAutoMatching(true);
    setError(null);
    try {
      const before = doc.shapes.filter((s) => s.pitch_id).length;
      const matched = await api.post<SitePlanVectorDocument>("/api/v1/site-plan/document/auto-match", doc);
      applyChange(matched, true);
      const after = matched.shapes.filter((s) => s.pitch_id).length;
      setNotice({
        kind: after > before ? "success" : "info",
        text:
          after > before
            ? `${after - before} Fläche(n) anhand der Beschriftung zugeordnet.`
            : "Keine weiteren eindeutigen Treffer – fehlende Zuordnungen bitte manuell setzen (Fläche wählen, Reiter „Plätze“).",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Automatische Zuordnung fehlgeschlagen.");
    } finally {
      setAutoMatching(false);
    }
  }

  function buildBackgroundSvg(): string {
    if (!doc) return "";
    return exportDocumentToSvg(doc, {
      includeLabels: true,
      pitchCodes,
      pitchCategoryColors,
      assignedPitchStyle: "neutral",
      sourceImage: embedSourceInBackground ? sourceImage : null,
      embedDocument: false,
    });
  }

  async function save() {
    if (!doc || !canEdit) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const body: SitePlanDocumentUpdate = {
        document: doc,
        sync_pitches: syncPitches,
        clear_unassigned_pitches: false,
      };
      if (sourceImageDirty === "replace" && sourceImage) body.source_image_data = sourceImage;
      if (sourceImageDirty === "clear") body.clear_source_image = true;
      if (publishBackground && isAdmin) body.background_svg = buildBackgroundSvg();
      const result = await api.put<SitePlanDocumentSaveResult>("/api/v1/site-plan/document", body);
      setSavedMeta({ updated_at: result.updated_at, updated_by: result.updated_by });
      setDirty(false);
      setSourceImageDirty("keep");
      const parts = ["Platzplan gespeichert."];
      if (result.synced_pitches) parts.push(`${result.synced_pitches} Platzfläche(n) in die Stellplätze übernommen.`);
      if (result.background_published) parts.push("Hintergrund für den Live-Platzplan veröffentlicht.");
      setNotice({ kind: "success", text: parts.join(" ") });
      if (result.synced_pitches) {
        api.get<Pitch[]>("/api/v1/pitches").then((list) => setPitches(list.filter((p) => p.is_active))).catch(() => undefined);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  function downloadSvg(withImage: boolean) {
    if (!doc) return;
    const svg = exportDocumentToSvg(doc, {
      includeLabels: true,
      pitchCodes,
      pitchCategoryColors,
      sourceImage: withImage ? sourceImage : null,
    });
    downloadTextFile(`platzplan${withImage ? "-mit-bild" : ""}.svg`, svg);
  }

  // --- Render ---------------------------------------------------------------------------
  const toolButton = (
    active: boolean,
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    disabled = false,
  ) => (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        "inline-flex h-9 min-w-9 items-center justify-center gap-1.5 rounded-lg px-2 text-sm transition disabled:opacity-40",
        active ? "bg-accent text-label-on-accent shadow-sm" : "text-label hover:bg-fill-tertiary",
      )}
    >
      {icon}
    </button>
  );

  const hasDocument = Boolean(doc);
  const assignedCount = doc?.shapes.filter((s) => s.pitch_id).length ?? 0;

  return (
    <div className="space-y-5">
      {!canEdit ? (
        <Alert variant="info" title="Nur Ansicht">
          Zum Bearbeiten des Platzplans ist die Rolle <strong className="font-medium">Camping-Rezeption</strong> oder{" "}
          <strong className="font-medium">Camping-Admin</strong> erforderlich.
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="Fehler">
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert variant={notice.kind}>
          <p>{notice.text}</p>
          {notice.details && notice.details.length > 0 ? (
            <ul className="mt-1 list-disc pl-5 text-xs text-label-secondary">
              {notice.details.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          ) : null}
        </Alert>
      ) : null}

      <Card
        title="Plan erstellen oder importieren"
        description="Ein vorhandenes Planbild per KI in bearbeitbare Flächen umwandeln, ein SVG importieren oder mit den bestehenden Platzflächen starten."
        bodyClassName="space-y-4"
      >
        {loading ? (
          <p className="text-sm text-label-secondary">Laden…</p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-3">
            {/* KI-Umwandlung */}
            <div className="flex flex-col gap-3 rounded-xl border border-separator p-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-accent" aria-hidden />
                <h3 className="text-sm font-semibold text-label">Planbild mit KI umwandeln</h3>
              </div>
              <p className="text-xs text-label-secondary">
                PNG/JPEG hochladen. Die KI erkennt Stellplatz-Flächen samt Nummern, Wege, Gebäude und Symbole und
                erzeugt einen bearbeitbaren Entwurf.
              </p>
              {aiStatus && !aiStatus.available ? (
                <p className="rounded-md bg-status-orange/10 px-2 py-1.5 text-xs text-label">
                  KI nicht konfiguriert: {aiStatus.reason ?? "SITE_PLAN_AI_PROVIDER/SITE_PLAN_AI_API_KEY setzen."} Das Bild kann
                  trotzdem als Vorlage zum Nachzeichnen genutzt werden.
                </p>
              ) : aiStatus?.available ? (
                <p className="text-[11px] text-label-tertiary">
                  Modell: {aiStatus.provider} / {aiStatus.model}
                </p>
              ) : null}
              <label
                className={clsx(
                  "eh-btn eh-btn--secondary inline-flex min-h-[40px] cursor-pointer items-center justify-center gap-2 px-3 text-sm font-medium",
                  !canEdit && "pointer-events-none opacity-50",
                )}
              >
                <ImageIcon className="h-4 w-4" aria-hidden />
                {pendingImage ? "Anderes Bild wählen" : "Planbild wählen"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  disabled={!canEdit || vectorizing}
                  onChange={(e) => {
                    void onImageFileSelected(e.target.files?.[0] ?? null);
                    e.target.value = "";
                  }}
                />
              </label>
              {pendingImage ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <img
                      src={pendingImage.dataUrl}
                      alt=""
                      className="h-16 w-24 rounded border border-separator object-contain bg-white"
                    />
                    <div className="min-w-0 text-xs text-label-secondary">
                      <p className="truncate font-medium text-label">{pendingImage.fileName}</p>
                      <p>
                        {pendingImage.width} × {pendingImage.height} px
                      </p>
                    </div>
                  </div>
                  <textarea
                    className="eh-input min-h-[60px] w-full text-sm"
                    placeholder="Hinweise für die KI (optional), z. B. „Nummern 1–74 und W1–W17; Legende: orange Stellplätze, rot Komfort, blau Komfort mit Mainblick, grün Dauerstellplätze“"
                    value={aiHints}
                    onChange={(e) => setAiHints(e.target.value)}
                    disabled={vectorizing}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      loading={vectorizing}
                      disabled={!canEdit || !aiStatus?.available}
                      leftIcon={<Sparkles className="h-4 w-4" aria-hidden />}
                      onClick={() => void vectorizeWithAi()}
                    >
                      Mit KI umwandeln
                    </Button>
                    <Button type="button" size="sm" variant="secondary" disabled={!canEdit || vectorizing} onClick={useImageAsBackgroundOnly}>
                      Nur als Vorlage verwenden
                    </Button>
                  </div>
                  {vectorizing ? (
                    <p className="text-xs text-label-secondary">
                      Die KI analysiert den Plan – das kann 30–90 Sekunden dauern…
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* SVG-Import */}
            <div className="flex flex-col gap-3 rounded-xl border border-separator p-4">
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4 text-accent" aria-hidden />
                <h3 className="text-sm font-semibold text-label">SVG-Platzplan importieren</h3>
              </div>
              <p className="text-xs text-label-secondary">
                Vektor-Datei (z. B. aus Inkscape, Illustrator oder einer externen KI-Umwandlung) hochladen. Polygone, Rechtecke
                und Pfade werden zu Flächen; Texte in Flächen zur Platznummer.
              </p>
              <label
                className={clsx(
                  "eh-btn eh-btn--secondary inline-flex min-h-[40px] cursor-pointer items-center justify-center gap-2 px-3 text-sm font-medium",
                  !canEdit && "pointer-events-none opacity-50",
                )}
              >
                <Upload className="h-4 w-4" aria-hidden />
                SVG-Datei wählen
                <input
                  type="file"
                  accept=".svg,image/svg+xml"
                  className="sr-only"
                  disabled={!canEdit}
                  onChange={(e) => {
                    void onSvgFileSelected(e.target.files?.[0] ?? null);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>

            {/* Bestehend / leer */}
            <div className="flex flex-col gap-3 rounded-xl border border-separator p-4">
              <div className="flex items-center gap-2">
                <Pentagon className="h-4 w-4 text-accent" aria-hidden />
                <h3 className="text-sm font-semibold text-label">Aus bestehendem Plan oder leer</h3>
              </div>
              <p className="text-xs text-label-secondary">
                Übernimmt die unter{" "}
                <Link to="/stammdaten/platzplan" className="text-accent underline">
                  Platzplan
                </Link>{" "}
                nachgezeichneten Flächen{legacyImage ? " und das Planbild als Vorlage" : ""}.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!canEdit || pitches.every((p) => !p.map_polygon?.length)}
                  onClick={() => void startFromExistingPlan()}
                >
                  Bestehende Flächen übernehmen
                </Button>
                <Button type="button" size="sm" variant="ghost" disabled={!canEdit} onClick={startBlank}>
                  Leeren Plan anlegen
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {!loading && doc ? (
        <Card
          title="Platzplan bearbeiten"
          description={
            savedMeta.updated_at
              ? `Zuletzt gespeichert ${new Date(savedMeta.updated_at).toLocaleString("de-DE")}${savedMeta.updated_by ? ` von ${savedMeta.updated_by}` : ""}${dirty ? " · ungespeicherte Änderungen" : ""}`
              : dirty
                ? "Noch nicht gespeichert"
                : undefined
          }
          bodyClassName="space-y-3"
        >
          {/* Werkzeugleiste */}
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-separator bg-card p-1.5">
            <div className="flex items-center gap-0.5">
              {toolButton(tool.kind === "select", "Auswählen (V)", <MousePointer2 className="h-4 w-4" />, () => setTool({ kind: "select" }))}
              {toolButton(tool.kind === "pan", "Ansicht verschieben (Leertaste halten)", <Hand className="h-4 w-4" />, () => setTool({ kind: "pan" }))}
              {toolButton(tool.kind === "polygon", "Fläche zeichnen (Polygon)", <Pentagon className="h-4 w-4" />, () => setTool({ kind: "polygon" }), !canEdit)}
              {toolButton(tool.kind === "rect", "Rechteck zeichnen", <Square className="h-4 w-4" />, () => setTool({ kind: "rect" }), !canEdit)}
              {toolButton(tool.kind === "text", "Text einfügen", <Type className="h-4 w-4" />, () => setTool({ kind: "text" }), !canEdit)}
              {toolButton(tool.kind === "route", "Anfahrt zeichnen", <Route className="h-4 w-4" />, () => setTool({ kind: "route", pitchId: null }), !canEdit)}
            </div>
            {(tool.kind === "polygon" || tool.kind === "rect") && (
              <label className="flex items-center gap-1.5 text-xs text-label-secondary">
                Neue Fläche als
                <select
                  className="eh-input h-8 text-xs"
                  value={newShapeLayer}
                  onChange={(e) => setNewShapeLayer(e.target.value as PlanLayer)}
                >
                  {PLAN_LAYERS.map((l) => (
                    <option key={l.key} value={l.key}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <span className="h-6 w-px bg-separator" aria-hidden />
            <div className="flex items-center gap-0.5">
              {toolButton(false, "Rückgängig (Strg+Z)", <Undo2 className="h-4 w-4" />, undo, !canUndo)}
              {toolButton(false, "Wiederholen (Strg+Y)", <Redo2 className="h-4 w-4" />, redo, !canRedo)}
            </div>
            <span className="h-6 w-px bg-separator" aria-hidden />
            <div className="flex items-center gap-0.5">
              {toolButton(false, "Vergrößern", <ZoomIn className="h-4 w-4" />, () => editorHandle.current?.zoomBy(1.25))}
              {toolButton(false, "Verkleinern", <ZoomOut className="h-4 w-4" />, () => editorHandle.current?.zoomBy(1 / 1.25))}
              {toolButton(false, "Ganzen Plan zeigen", <Maximize2 className="h-4 w-4" />, () => editorHandle.current?.fitToView())}
            </div>
            <span className="h-6 w-px bg-separator" aria-hidden />
            <label className="flex items-center gap-1.5 text-xs text-label-secondary">
              <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} />
              Nummern
            </label>
            {duplicateLabelCount > 0 && canEdit ? (
              <button
                type="button"
                className="eh-btn eh-btn--secondary eh-btn--sm"
                onClick={() => {
                  if (!doc) return;
                  applyChange(
                    { ...doc, texts: doc.texts.filter((text) => !isDuplicateLabelText(text, doc, pitchCodes)) },
                    true,
                  );
                }}
              >
                {duplicateLabelCount} doppelte Beschriftung{duplicateLabelCount === 1 ? "" : "en"} entfernen
              </button>
            ) : null}
            {sourceImage ? (
              <label className="flex items-center gap-1.5 text-xs text-label-secondary">
                <input type="checkbox" checked={showSourceImage} onChange={(e) => setShowSourceImage(e.target.checked)} />
                Vorlage
                <input
                  type="range"
                  min={0.1}
                  max={1}
                  step={0.1}
                  value={sourceOpacity}
                  disabled={!showSourceImage}
                  onChange={(e) => setSourceOpacity(Number.parseFloat(e.target.value))}
                  className="w-20"
                  aria-label="Deckkraft der Vorlage"
                />
              </label>
            ) : null}
            <span className="ml-auto text-xs text-label-tertiary">
              {doc.shapes.length} Flächen · {doc.symbols.length} Symbole · {(doc.routes ?? []).length} Anfahrten · {assignedCount}/{pitches.length} Plätze zugeordnet
            </span>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row">
            <SitePlanVectorEditor
              document={doc}
              onChange={applyChange}
              tool={tool}
              onToolChange={setTool}
              selection={selection}
              onSelectionChange={setSelection}
              pitchCodes={pitchCodes}
              pitchCategoryColors={pitchCategoryColors}
              sourceImage={sourceImage}
              showSourceImage={showSourceImage}
              sourceImageOpacity={sourceOpacity}
              showLabels={showLabels}
              readOnly={!canEdit}
              newShapeLayer={newShapeLayer}
              revealedPitchId={revealedPitchId}
              onRouteFinished={(route) => {
                if (route.pitch_id) setRevealedPitchId(route.pitch_id);
              }}
              handleRef={editorHandle}
              className="h-[60vh] min-h-[360px] flex-1 lg:h-[72vh]"
            />
            <SitePlanEditorSidebar
              document={doc}
              selection={selection}
              onSelectionChange={setSelection}
              tool={tool}
              onToolChange={setTool}
              pitches={pitches}
              canEdit={canEdit}
              onUpdateShape={patchShape}
              onUpdateSymbol={patchSymbol}
              onUpdateText={patchText}
              onUpdateRoute={patchRoute}
              onDelete={deleteElement}
              revealedPitchId={revealedPitchId}
              onRevealPitch={setRevealedPitchId}
              onHideApproach={() => {
                setRevealedPitchId(null);
                setSelection((current) => {
                  if (current?.kind !== "route") return current;
                  const route = (docStateRef.current?.routes ?? []).find((item) => item.id === current.id);
                  return route?.pitch_id ? null : current;
                });
              }}
              onAutoMatch={() => void autoMatch()}
              autoMatching={autoMatching}
            />
          </div>

          {/* Speichern / Export */}
          <div className="flex flex-col gap-3 rounded-xl border border-separator bg-fill-tertiary/40 p-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="flex flex-col gap-1.5 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={syncPitches} disabled={!canEdit} onChange={(e) => setSyncPitches(e.target.checked)} />
                <span>
                  Zugeordnete Flächen in die Stellplätze übernehmen{" "}
                  <span className="text-xs text-label-tertiary">(Live-Platzplan, Dashboard, Gäste-Tablet)</span>
                </span>
              </label>
              <label className={clsx("flex items-center gap-2", !isAdmin && "opacity-60")}>
                <input
                  type="checkbox"
                  checked={publishBackground}
                  disabled={!isAdmin}
                  onChange={(e) => setPublishBackground(e.target.checked)}
                />
                <span>
                  Als Hintergrund des Live-Platzplans veröffentlichen{" "}
                  <span className="text-xs text-label-tertiary">
                    {isAdmin ? "(ersetzt das bisherige Planbild durch das SVG)" : "(nur Camping-Admin)"}
                  </span>
                </span>
              </label>
              {publishBackground && sourceImage ? (
                <label className="ml-6 flex items-center gap-2 text-xs text-label-secondary">
                  <input
                    type="checkbox"
                    checked={embedSourceInBackground}
                    onChange={(e) => setEmbedSourceInBackground(e.target.checked)}
                  />
                  Vorlagenbild in den Hintergrund einbetten
                </label>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              <Button
                type="button"
                variant="secondary"
                leftIcon={<Download className="h-4 w-4" aria-hidden />}
                onClick={() => downloadSvg(false)}
              >
                SVG herunterladen
              </Button>
              {sourceImage ? (
                <Button type="button" variant="ghost" onClick={() => downloadSvg(true)}>
                  SVG mit Vorlagenbild
                </Button>
              ) : null}
              <Button type="button" loading={saving} disabled={!canEdit || !hasDocument} onClick={() => void save()}>
                {dirty ? "Speichern" : "Erneut speichern"}
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {!loading && !doc ? (
        <Card title="Noch kein Vektor-Platzplan" bodyClassName="text-sm text-label-secondary">
          Beginnen Sie oben mit einem Planbild (KI-Umwandlung), einem SVG-Import oder den bestehenden Platzflächen.
        </Card>
      ) : null}

      <Modal
        open={replaceConfirm !== null}
        onClose={() => setReplaceConfirm(null)}
        title={replaceConfirm?.title ?? "Plan ersetzen?"}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setReplaceConfirm(null)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => {
                const run = replaceConfirm?.run;
                setReplaceConfirm(null);
                run?.();
              }}
            >
              Ersetzen
            </Button>
          </div>
        }
      >
        <p className="text-sm text-label-secondary">
          Der aktuelle Plan im Editor wird ersetzt. Gespeicherte Daten bleiben erhalten, bis Sie erneut speichern; der
          Schritt lässt sich mit „Rückgängig“ nicht zurücknehmen.
        </p>
      </Modal>
    </div>
  );
}
